import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { ApiService } from '@mm-aggregator/drf-dynamics';

/** Single scope option for editors (from ``allowed_scope_choices`` on the API). */
export interface ScopeChoice {
  value: string;
  label: string;
}

/**
 * MerchantApplication model from the backend.
 */
export interface MerchantApplication {
  id: string;
  merchant: string;
  merchant_name: string;
  name: string;
  description: string;
  client_id: string;
  environment: string;
  allowed_ips: string[];
  allowed_scopes: string[];
  /** Custom SMS sender names the application may request on the send-SMS API (back-office managed). */
  allowed_sms_sender_names?: string[];
  /** Present on GET — lists valid scope values and labels for UIs. */
  allowed_scope_choices?: ScopeChoice[];
  is_active: boolean;
  active_key_count: number;
  last_used_at: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * APIKey model from the backend.
 */
/** Body of `POST auth/merchant-applications/`. */
export interface ApplicationCreatePayload {
  merchant: string;
  name: string;
  description?: string;
  environment: string;
  allowed_ips?: string[];
  allowed_scopes?: string[];
  is_active?: boolean;
}

export interface APIKey {
  id: string;
  application: string;
  application_name: string;
  application_client_id: string;
  name: string;
  secret_prefix: string;
  is_active: boolean;
  is_expired: boolean;
  expires_at: string | null;
  last_used_at: string | null;
  created_at: string;
  created_by: string | null;
  /** Only present immediately after creation. */
  secret?: string;
  /** Warning message after creation. */
  warning?: string;
}

/**
 * Transaction stats for a single type.
 */
export interface TxStats {
  total: number;
  success: number;
  failed: number;
  pending: number;
  success_amount: number;
}

/**
 * Application stats response.
 */
export interface ApplicationStats {
  period: string;
  transactions: {
    deposits: TxStats;
    payouts: TxStats;
    refunds: TxStats;
    remittances: TxStats;
  };
  api_keys: {
    total: number;
    active: number;
  };
  recent_audit: AuditEntry[];
}

/**
 * Wallet currency breakdown.
 */
export interface WalletByCurrency {
  currency__code: string;
  count: number;
  balance: number;
  reserved?: number;
}

/**
 * Merchant BI stats response.
 */
export interface MerchantStats {
  period: string;
  merchant: { id: string; name: string };
  transactions: {
    deposits: TxStats;
    payouts: TxStats;
    refunds: TxStats;
    remittances: TxStats;
  };
  applications: { total: number; active: number };
  api_keys: { total: number; active: number };
  wallets: {
    total: number;
    active: number;
    total_balance: number;
    total_reserved: number;
  };
  wallets_by_currency: WalletByCurrency[];
  wallet_differences: Array<{
    wallet_id: string;
    currency_code: string;
    country_code: string;
    wallet_balance: number;
    ledger_balance: number;
    difference: number;
    is_tagged_mismatch: boolean;
    mismatch_reason: string;
  }>;
  recent_audit: AuditEntry[];
}

/**
 * API key stats response.
 */
export interface KeyStats {
  period: string;
  key_id: string;
  key_name: string;
  is_active: boolean;
  last_used_at: string | null;
  created_at: string;
  expires_at: string | null;
  audit_event_count: number;
  recent_audit: AuditEntry[];
}

/**
 * Audit log entry.
 */
export interface AuditEntry {
  id: number;
  action: string;
  entity_type: string;
  entity_id: string;
  timestamp: string;
  user_id: string | null;
  metadata: Record<string, unknown>;
}

/**
 * Paginated list response.
 */
export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

/**
 * Service for managing Merchant Applications and API Keys.
 */
@Injectable({
  providedIn: 'root',
})
export class MerchantApplicationService {
  private readonly api = inject(ApiService);
  private readonly appPath = 'auth/merchant-applications';
  private readonly keyPath = 'auth/api-keys';

  // ─── Merchant ───────────────────────────────────────────────

  /**
   * Get a merchant by ID.
   */
  getMerchant(id: string): Observable<Record<string, unknown>> {
    return this.api.get<Record<string, unknown>>(`auth/merchants/${id}/`);
  }

  /**
   * Get merchant applications.
   */
  getMerchantApplications(merchantId: string): Observable<MerchantApplication[]> {
    return this.api.get<MerchantApplication[]>(`auth/merchants/${merchantId}/applications/`);
  }

  /**
   * Get merchant BI stats.
   */
  getMerchantStats(merchantId: string, period: string = '30d'): Observable<MerchantStats> {
    return this.api.get<MerchantStats>(`auth/merchants/${merchantId}/stats/`, { period });
  }

  /**
   * Update merchant webhook URL.
   */
  updateMerchantWebhook(
    merchantId: string,
    data: { webhook_url: string },
  ): Observable<{ message: string; webhook_url: string; has_webhook_secret: boolean }> {
    return this.api.post<{ message: string; webhook_url: string; has_webhook_secret: boolean }>(
      `auth/merchants/${merchantId}/update-webhook/`,
      data,
    );
  }

  /**
   * Regenerate merchant webhook secret. Returns plain-text secret once.
   */
  regenerateWebhookSecret(
    merchantId: string,
  ): Observable<{ message: string; webhook_secret: string; has_webhook_secret: boolean }> {
    return this.api.post<{ message: string; webhook_secret: string; has_webhook_secret: boolean }>(
      `auth/merchants/${merchantId}/regenerate-webhook-secret/`,
      {},
    );
  }

  /**
   * Update merchant (partial update).
   */
  updateMerchant(merchantId: string, data: Record<string, unknown>): Observable<Record<string, unknown>> {
    return this.api.patch<Record<string, unknown>>(`auth/merchants/${merchantId}/`, data);
  }

  // ─── Application CRUD ──────────────────────────────────────

  /**
   * Get a single application by ID.
   */
  get(id: string): Observable<MerchantApplication> {
    return this.api.get<MerchantApplication>(`${this.appPath}/${id}/`);
  }

  /**
   * Update an application (partial).
   */
  create(data: ApplicationCreatePayload): Observable<MerchantApplication> {
    return this.api.post<MerchantApplication>(`${this.appPath}/`, data);
  }

  /**
   * Update an application.
   */
  update(id: string, data: Partial<MerchantApplication>): Observable<MerchantApplication> {
    return this.api.patch<MerchantApplication>(`${this.appPath}/${id}/`, data);
  }

  /**
   * Activate an application.
   */
  activate(id: string): Observable<MerchantApplication> {
    return this.api.post<MerchantApplication>(`${this.appPath}/${id}/activate/`, {});
  }

  /**
   * Deactivate an application.
   */
  deactivate(id: string): Observable<MerchantApplication> {
    return this.api.post<MerchantApplication>(`${this.appPath}/${id}/deactivate/`, {});
  }

  /**
   * Get application activity stats.
   */
  getStats(id: string, period: string = '30d'): Observable<ApplicationStats> {
    return this.api.get<ApplicationStats>(`${this.appPath}/${id}/stats/`, { period });
  }

  // ─── API Keys ──────────────────────────────────────────────

  /**
   * List API keys for an application.
   */
  getApiKeys(applicationId: string): Observable<APIKey[]> {
    return this.api.get<APIKey[]>(`${this.appPath}/${applicationId}/api_keys/`);
  }

  /**
   * Create a new API key. Returns the secret once in the response.
   */
  createApiKey(data: { application: string; name: string; expires_at?: string | null }): Observable<APIKey> {
    return this.api.post<APIKey>(`${this.keyPath}/`, data);
  }

  /**
   * Activate an API key.
   */
  activateKey(keyId: string): Observable<APIKey> {
    return this.api.post<APIKey>(`${this.keyPath}/${keyId}/activate/`, {});
  }

  /**
   * Deactivate an API key.
   */
  deactivateKey(keyId: string): Observable<APIKey> {
    return this.api.post<APIKey>(`${this.keyPath}/${keyId}/deactivate/`, {});
  }

  /**
   * Rotate an API key (creates new, deactivates old).
   */
  rotateKey(keyId: string, deactivateOld: boolean = true): Observable<APIKey> {
    return this.api.post<APIKey>(`${this.keyPath}/${keyId}/rotate/`, { deactivate_old: deactivateOld });
  }

  /**
   * Delete an API key.
   */
  deleteKey(keyId: string): Observable<void> {
    return this.api.delete(`${this.keyPath}/${keyId}/`);
  }

  /**
   * Get API key activity stats.
   */
  getKeyStats(keyId: string, period: string = '30d'): Observable<KeyStats> {
    return this.api.get<KeyStats>(`${this.keyPath}/${keyId}/stats/`, { period });
  }
}
